import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapLocationDot } from "@fortawesome/free-solid-svg-icons";

import { getRuta } from "../../../helpers/Helpers";
import { getToken } from "../../../api/Account";
import { getDepartmentListAsync } from "../../../api/Locations";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
} from "../../../components/base";
import { encabezadoTabla, ocultoVisual, useCargaLocal } from "../comunes";

// Son los 17 departamentos y regiones del país: caben en una sola tabla, sin páginas.
function MainLocations() {
  const [departmentList, setDepartmentList] = useState([]);
  const [recargar, setRecargar] = useState(false);
  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const { cargando, error, cargar } = useCargaLocal(() => getDepartmentListAsync(token));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setDepartmentList(result.data.result || []);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar]);

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={8} columnas={3} etiqueta="Cargando departamentos…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los departamentos"
        }
        onReintentar={() => setRecargar((actual) => !actual)}
      />
    );
  } else if (departmentList.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="No hay departamentos"
        descripcion="Aquí aparecen los departamentos con sus municipios y comunidades."
      />
    );
  } else {
    contenido = (
      <TableContainer>
        <Table aria-label="Departamentos" sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoTabla}>Departamento</TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Municipios</TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                <Box component="span" sx={ocultoVisual}>
                  Ver
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {departmentList.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => navigate(`${ruta}/department/${row.id}`)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
                  {row.name}
                </TableCell>
                <TableCell sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {row.municipalities?.length ?? 0}
                </TableCell>
                <TableCell sx={{ textAlign: "right", width: 56 }}>
                  <Tooltip title="Ver municipios">
                    <IconButton
                      aria-label={`Ver los municipios de ${row.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${ruta}/department/${row.id}`);
                      }}
                      sx={{ color: "primary.main" }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Ubicaciones"
        subtitulo="Departamentos, municipios y comunidades donde viven los clientes"
        icono={<FontAwesomeIcon icon={faMapLocationDot} />}
      />
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>{contenido}</Paper>
    </Container>
  );
}

export default MainLocations;
